import { applyGermanize, germanize } from './germanize.js';
import { useIsMobile, usePersistentState } from './hooks.js';
import { getNextPathStation } from './logic.js';
import { LoeschNotiz, Notizen } from './reflexion.jsx';
import { Disclosure, MusterFlipCard } from './shared.jsx';
import { accent, accentBorder, accentDim, border, borderLight, danger, pillBtn, surface, surface2, textDim, textMuted } from './tokens.js';

const { useState, useEffect } = React;

// ─── ErkennenPanel ───────────────────────────────────────────────────────
export function ErkennenPanel({ pattern }) {
  const mobile = useIsMobile();
  const p = pattern;

  return (
    <section style={{
      maxWidth: 640, margin: "0 auto",
      padding: mobile ? "24px 20px 64px" : "24px 24px 80px"
    }}>
      {/* Hero */}
      <header style={{marginBottom: 28}}>
        <div style={{color: textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8}}>
          {applyGermanize(p.subtitle)}
        </div>
        <h2 style={{
          color: "#e8e6e3", fontSize: mobile ? 26 : 32,
          fontWeight: 400, lineHeight: 1.2, marginBottom: 4
        }}>
          {applyGermanize(p.title)}
          {p.titleEm && (
            <span style={{
              color: accent, fontStyle: "italic",
              marginLeft: 12, fontSize: "0.7em", fontWeight: 300
            }}>
              {p.titleEm}
            </span>
          )}
        </h2>
        <p style={{
          color: textMuted, fontFamily: "Georgia, serif",
          marginTop: 14, lineHeight: 1.65, fontSize: mobile ? 15 : 16
        }}>
          {applyGermanize(p.goal)}
        </p>
      </header>

      {/* Herkunft */}
      {p.quelle && (
        <section style={{marginBottom: 28}}>
          <h3 style={{
            color: accent, fontSize: 11, fontFamily: "'Raleway', system-ui, sans-serif",
            textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10
          }}>
            {applyGermanize("Herkunft")}
          </h3>
          <div style={{color: textDim, fontFamily: "Georgia, serif", fontSize: 13}}>
            {applyGermanize(p.quelle)}
          </div>
        </section>
      )}

      {/* FlipCard */}
      <MusterFlipCard pattern={p} />

      {/* Reflexion (muster-spezifisch) */}
      <section style={{marginTop: 28, marginBottom: 28}}>
        <h3 style={{
          color: accent, fontSize: 11, fontFamily: "'Raleway', system-ui, sans-serif",
          textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10
        }}>
          {applyGermanize("Reflexion")}
        </h3>
        <p style={{
          color: textMuted, fontFamily: "Georgia, serif",
          fontSize: mobile ? 15 : 16, lineHeight: 1.6,
          fontStyle: "italic", marginBottom: 4
        }}>
          {applyGermanize("In welchen Situationen beobachtest du das Muster bei dir selbst?")}
        </p>
        <Notizen storageKey={`${p.id}.erkennen.selbstspiegel`} />
      </section>

      {/* Reaktion als auffahrender Container */}
      <Disclosure
        label={applyGermanize("Reaktionsmoeglichkeiten")}
        storageKey={`${p.id}.erkennen.reactionOpen`}
        defaultOpen={false}>
        <ul style={{
          paddingLeft: 20, color: "#e8e6e3",
          lineHeight: 1.7, fontFamily: "Georgia, serif",
          fontSize: mobile ? 15 : 16, marginTop: 4
        }}>
          {p.reaction.map((r, i) => <li key={i} style={{marginBottom: 10}}>{applyGermanize(r)}</li>)}
        </ul>
      </Disclosure>

      {/* Kontext-Tags */}
      <section style={{marginTop: 28}}>
        <h3 style={{
          color: accent, fontSize: 11, fontFamily: "'Raleway', system-ui, sans-serif",
          textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10
        }}>
          {applyGermanize("Typischer Kontext")}
        </h3>
        <div style={{display: "flex", flexWrap: "wrap", gap: 6}}>
          {p.contexts.map((c, i) => (
            <span key={i} style={{
              padding: "4px 10px", background: surface2, color: textMuted,
              borderRadius: 999, fontSize: 12, border: `1px solid ${borderLight}`
            }}>
              {applyGermanize(c)}
            </span>
          ))}
        </div>
      </section>
    </section>
  );
}

// ─── UnterscheidenPanel ──────────────────────────────────────────────────
export function UnterscheidenPanel({ pattern, onJumpTo, onProgress }) {
  const mobile = useIsMobile();
  const p = pattern;

  const rawSnippets = Array.isArray(p.dialogSnippets) ? p.dialogSnippets : [];
  // Pro Pattern einmal mischen — bei Pattern-Wechsel neue Reihenfolge
  const snippets = React.useMemo(() => {
    const arr = [...rawSnippets];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [p.id]);
  const [idx, setIdx] = usePersistentState(`${p.id}.unterscheiden.idx`, 0);
  const [answeredSet, setAnsweredSet] = usePersistentState(`${p.id}.unterscheiden.answered`, []);
  const [answered, setAnswered] = useState(false);
  const [userSaidThis, setUserSaidThis] = useState(null);

  useEffect(() => { setAnswered(false); setUserSaidThis(null); }, [p.id, idx]);
  useEffect(() => { if (onProgress) onProgress(); }, [answeredSet]);

  if (snippets.length === 0) {
    return (
      <section style={{
        maxWidth: 560, margin: "0 auto",
        padding: mobile ? "48px 20px" : "64px 24px", textAlign: "center"
      }}>
        <p style={{
          color: textDim, fontFamily: "Georgia, serif",
          fontSize: 15, lineHeight: 1.7, fontStyle: "italic"
        }}>
          {applyGermanize(`Der Unterscheiden-Modus fuer ${p.title} ist noch nicht ausgearbeitet.`)}
        </p>
        <p style={{color: textDim, fontFamily: "Georgia, serif", fontSize: 14, lineHeight: 1.6, marginTop: 14}}>
          {applyGermanize("Im Erkennen-Modus findest du das Muster mit Beispiel und Erkennungsmerkmalen.")}
        </p>
      </section>
    );
  }

  const safeIdx = Math.min(idx, snippets.length - 1);
  const snippet = snippets[safeIdx];
  const total   = snippets.length;
  const correct = answered && userSaidThis === snippet.isThis;
  // Pro Snippet: explizit gesetztes Muster oder Fallback auf similarTo[0]
  const fallbackId = Array.isArray(p.similarTo) && p.similarTo.length > 0 ? p.similarTo[0] : null;
  const otherId    = snippet.otherPattern || fallbackId;
  const other      = otherId ? PATTERNS[otherId] : null;

  function answer(saidThis) {
    setUserSaidThis(saidThis);
    setAnswered(true);
    const origIdx = rawSnippets.indexOf(snippet);
    if (origIdx >= 0 && !answeredSet.includes(origIdx)) {
      setAnsweredSet([...answeredSet, origIdx]);
    }
  }
  function next() {
    setAnswered(false);
    setUserSaidThis(null);
    setIdx((safeIdx + 1) % total);
  }

  return (
    <section style={{
      maxWidth: 640, margin: "0 auto",
      padding: mobile ? "24px 20px 64px" : "24px 24px 80px"
    }}>
      <div style={{
        color: textDim, fontSize: 11, textTransform: "uppercase",
        letterSpacing: 1.5, marginBottom: 14, textAlign: "center"
      }}>
        {applyGermanize(`Unterscheiden — ${safeIdx + 1} von ${total}`)}
      </div>

      <div style={{
        background: surface, border: `1px solid ${border}`, borderRadius: 10,
        padding: mobile ? "22px 20px" : "28px 26px",
        fontFamily: "Georgia, serif", fontSize: mobile ? 16 : 17,
        lineHeight: 1.7, color: "#e8e6e3",
        whiteSpace: "pre-wrap", marginBottom: 24
      }}>
        {applyGermanize(snippet.snippet)}
      </div>

      {!answered && (
        <div style={{
          display: "flex", gap: 10,
          flexDirection: mobile ? "column" : "row",
          justifyContent: "center"
        }}>
          <button onClick={() => answer(true)} style={pillBtn(false)}>
            {applyGermanize(`Das ist ${p.title}`)}
          </button>
          <button onClick={() => answer(false)} style={pillBtn(false)}>
            {applyGermanize("Anderes Muster")}
          </button>
        </div>
      )}

      {answered && (() => {
        const statusText = correct
          ? (snippet.isThis ? `Stimmt – das ist ${p.title}.` : `Stimmt – das ist ein anderes Muster.`)
          : (snippet.isThis ? `Knapp daneben – das ist ${p.title}.` : `Knapp daneben – das ist ein anderes Muster.`);
        // Erklaerung mit {other}-Marker — wird durch klickbaren Pattern-Link ersetzt
        function renderExplanation() {
          if (!snippet.explanation) return null;
          const text = germanize(snippet.explanation);
          if (!other || !text.includes("{other}")) return text;
          const parts = text.split("{other}");
          return parts.map((part, i) => (
            <React.Fragment key={i}>
              {part}
              {i < parts.length - 1 && (
                <button onClick={() => onJumpTo(other.id)} style={{
                  background: "none", border: "none", color: accent,
                  textDecoration: "underline", cursor: "pointer",
                  padding: 0, fontFamily: "inherit", fontSize: "inherit"
                }}>
                  {germanize(other.title)}
                </button>
              )}
            </React.Fragment>
          ));
        }
        return (
          <div>
            <div style={{
              padding: "14px 18px", borderRadius: 8,
              background: correct ? accentDim : "rgba(232,122,122,0.10)",
              border: `1px solid ${correct ? accentBorder : "rgba(232,122,122,0.28)"}`,
              marginBottom: 16, fontFamily: "Georgia, serif", fontSize: 15, lineHeight: 1.6
            }}>
              <div style={{color: correct ? accent : danger, fontWeight: 600}}>
                {applyGermanize(statusText)}
              </div>
              {snippet.explanation && (
                <div style={{marginTop: 8, color: "#e8e6e3", lineHeight: 1.65}}>
                  {renderExplanation()}
                </div>
              )}
              {/* Fallback: solange keine explanation kuriert, weiterhin Vermutlich-Hinweis */}
              {!snippet.explanation && !snippet.isThis && other && (
                <div style={{marginTop: 8, fontSize: 14, color: "#e8e6e3"}}>
                  {applyGermanize("Vermutlich: ")}
                  <button onClick={() => onJumpTo(other.id)} style={{
                    background: "none", border: "none", color: accent,
                    textDecoration: "underline", cursor: "pointer",
                    padding: 0, fontFamily: "inherit", fontSize: "inherit"
                  }}>
                    {applyGermanize(other.title)}
                  </button>
                </div>
              )}
            </div>
            <div style={{textAlign: "center"}}>
              <button onClick={next} style={pillBtn(true)}>
                {applyGermanize(safeIdx + 1 < total ? "Naechster Dialog" : "Von vorne")}
              </button>
            </div>
          </div>
        );
      })()}
    </section>
  );
}

// ─── ReagierenPanel ──────────────────────────────────────────────────────
export function ReagierenPanel({ pattern, onSelectWithMode, onProgress }) {
  const mobile = useIsMobile();
  const p = pattern;
  const scenarios = Array.isArray(p.scenarios) ? p.scenarios : [];

  const [scIdx, setScIdx]   = usePersistentState(`${p.id}.reagieren.scIdx`, 0);
  const [chosen, setChosen] = usePersistentState(`${p.id}.reagieren.chosen`, {});
  const [eigeneReaktion, setEigeneReaktion] = usePersistentState(`${p.id}.reagieren.eigeneReaktion`, {});
  const [shownOptions, setShownOptions] = usePersistentState(`${p.id}.reagieren.shownOptions`, {});
  const [reselecting, setReselecting] = useState({});
  useEffect(() => { if (onProgress) onProgress(); }, [chosen]);

  if (scenarios.length === 0) {
    return (
      <section style={{
        maxWidth: 560, margin: "0 auto",
        padding: mobile ? "48px 20px" : "64px 24px", textAlign: "center"
      }}>
        <p style={{
          color: textDim, fontFamily: "Georgia, serif",
          fontSize: 15, lineHeight: 1.7, fontStyle: "italic"
        }}>
          {applyGermanize(`Der Reagieren-Modus fuer ${p.title} ist noch nicht ausgearbeitet.`)}
        </p>
        <p style={{color: textDim, fontFamily: "Georgia, serif", fontSize: 14, lineHeight: 1.6, marginTop: 14}}>
          {applyGermanize("Im Erkennen-Modus findest du Reaktionsempfehlungen aus dem Konzept.")}
        </p>
      </section>
    );
  }

  const safeIdx = Math.min(scIdx, scenarios.length - 1);
  const sc = scenarios[safeIdx];
  const chosenIdx = (chosen && typeof chosen === "object") ? chosen[safeIdx] : undefined;
  const hasChosen = chosenIdx !== undefined && chosenIdx !== null;
  const myReaction = (eigeneReaktion && typeof eigeneReaktion === "object") ? (eigeneReaktion[safeIdx] || "") : "";
  const optionsShown = !!(shownOptions && typeof shownOptions === "object" && shownOptions[safeIdx]);
  const isReselecting = !!(reselecting && reselecting[safeIdx]);

  function pick(i) {
    setChosen({ ...chosen, [safeIdx]: i });
    setReselecting({ ...reselecting, [safeIdx]: false });
  }
  // Andere Option ansehen: blendet die Liste wieder ein, ohne die Wahl zu loeschen
  function reselect() {
    setReselecting({ ...reselecting, [safeIdx]: true });
  }
  function setMyReaction(text) {
    setEigeneReaktion({ ...eigeneReaktion, [safeIdx]: text });
  }
  function revealOptions() {
    setShownOptions({ ...shownOptions, [safeIdx]: true });
  }

  return (
    <section style={{
      maxWidth: 640, margin: "0 auto",
      padding: mobile ? "24px 20px 64px" : "24px 24px 80px"
    }}>
      {/* Szenario-Auswahl falls mehrere */}
      {scenarios.length > 1 && (
        <div style={{
          display: "flex", gap: 6, marginBottom: 18,
          justifyContent: "center", flexWrap: "wrap"
        }}>
          {scenarios.map((s, i) => (
            <button key={i}
                    onClick={() => setScIdx(i)}
                    style={{
                      ...pillBtn(i === safeIdx),
                      padding: "5px 12px", fontSize: 12
                    }}>
              {applyGermanize(s.badge)}
            </button>
          ))}
        </div>
      )}

      {/* Setting-Hinweis */}
      {sc.setting && (
        <div style={{
          textAlign: "center", color: textDim, fontSize: 11,
          marginBottom: 12, textTransform: "uppercase", letterSpacing: 1.5
        }}>
          {applyGermanize(sc.setting)}
        </div>
      )}

      {/* Kontext + Prompt */}
      <div style={{
        background: surface, border: `1px solid ${border}`, borderRadius: 10,
        padding: mobile ? "22px 20px" : "28px 26px", marginBottom: 20,
        fontFamily: "Georgia, serif", color: "#e8e6e3"
      }}>
        <p style={{lineHeight: 1.7, marginBottom: 14, fontSize: mobile ? 15 : 16}}>
          {applyGermanize(sc.context)}
        </p>
        <p style={{color: accent, fontStyle: "italic", lineHeight: 1.6, fontSize: mobile ? 15 : 16}}>
          {applyGermanize(sc.prompt)}
        </p>
      </div>

      {/* Eigene Reaktion */}
      <div style={{marginBottom: 20}}>
        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10}}>
          <h3 style={{
            color: accent, fontSize: 11, fontFamily: "'Raleway', system-ui, sans-serif",
            textTransform: "uppercase", letterSpacing: 1.5, margin: 0
          }}>
            {applyGermanize("Deine Reaktion")}
          </h3>
          {myReaction.trim().length > 0 && (
            <LoeschNotiz variant="text" onConfirm={() => setMyReaction("")} />
          )}
        </div>
        <textarea
          value={myReaction}
          onChange={e => setMyReaction(e.target.value)}
          placeholder={applyGermanize("Wie wuerdest du darauf reagieren? Optional, deine Worte vor den Optionen.")}
          style={{
            width: "100%", minHeight: 84, padding: "12px 14px",
            background: surface, border: `1px solid ${border}`, borderRadius: 8,
            color: "#e8e6e3", fontFamily: "Georgia, serif",
            fontSize: mobile ? 14 : 15, lineHeight: 1.6,
            resize: "vertical", boxSizing: "border-box", outline: "none",
            caretColor: accent
          }}
        />
        {!optionsShown && (
          <div style={{textAlign: "center", marginTop: 14}}>
            <button onClick={revealOptions} style={pillBtn(true)}>
              {applyGermanize("Weitere Optionen ansehen")}
            </button>
          </div>
        )}
      </div>

      {/* Optionen */}
      {optionsShown && (!hasChosen || isReselecting) && (
        <div style={{display: "grid", gap: 10, marginBottom: 24}}>
          {sc.options.map((opt, i) => {
            const isCurrent = chosenIdx === i;
            return (
            <button key={i}
                    onClick={() => pick(i)}
                    style={{
                      textAlign: "left", padding: "14px 18px", borderRadius: 8,
                      background: isCurrent ? accentDim : surface,
                      border: `1px solid ${isCurrent ? accentBorder : border}`,
                      color: isCurrent ? accent : textMuted, cursor: "pointer",
                      fontFamily: "'Raleway', system-ui, sans-serif",
                      fontSize: mobile ? 14 : 15, lineHeight: 1.55,
                      transition: "border-color 0.2s, color 0.2s"
                    }}
                    onMouseEnter={e => {
                      if (isCurrent) return;
                      e.currentTarget.style.borderColor = borderLight;
                      e.currentTarget.style.color = "#e8e6e3";
                    }}
                    onMouseLeave={e => {
                      if (isCurrent) return;
                      e.currentTarget.style.borderColor = border;
                      e.currentTarget.style.color = textMuted;
                    }}>
              {applyGermanize(opt)}
            </button>
            );
          })}
        </div>
      )}

      {/* Gewaehlte Option + Reflexion */}
      {optionsShown && hasChosen && !isReselecting && (
        <div>
          <div style={{
            padding: "14px 18px", borderRadius: 8,
            background: accentDim, border: `1px solid ${accentBorder}`,
            color: accent, marginBottom: 16,
            fontFamily: "'Raleway', system-ui, sans-serif",
            fontSize: mobile ? 14 : 15, lineHeight: 1.55
          }}>
            {applyGermanize(sc.options[chosenIdx])}
          </div>

          <h3 style={{
            color: accent, fontSize: 11, fontFamily: "'Raleway', system-ui, sans-serif",
            textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10
          }}>
            {applyGermanize("Perspektive")}
          </h3>
          <div style={{
            padding: "16px 20px", background: surface2,
            border: `1px solid ${border}`, borderRadius: 8,
            fontFamily: "Georgia, serif", lineHeight: 1.7,
            color: "#e8e6e3", fontSize: mobile ? 15 : 16, marginBottom: 20
          }}>
            {applyGermanize(sc.optionReflections[chosenIdx]?.lens || "")}
          </div>

          <div style={{textAlign: "center"}}>
            <button onClick={reselect} style={pillBtn(false)}>
              {applyGermanize("Andere Option waehlen")}
            </button>
          </div>
        </div>
      )}

      {safeIdx === scenarios.length - 1 && getNextPathStation(p.id) && (
        <div style={{marginTop: 24, paddingTop: 18, borderTop: `1px solid ${border}`}}>
          <div style={{fontSize: 11, color: textDim, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8}}>
            {applyGermanize("Naechste Station im Pfad")}
          </div>
          <button onClick={() => { const n = getNextPathStation(p.id); if (n && onSelectWithMode) onSelectWithMode(n.id, n.mode); }}
            style={pillBtn(true)}>
            {applyGermanize(getNextPathStation(p.id).title)}
          </button>
        </div>
      )}
    </section>
  );
}
